import React, { useEffect } from 'react';
import { MixAndMatchBundle } from 'src/MixAndMatchBundle/MixAndMatchBundle';
import { useSetResolved } from 'src/core/engine/router';
import { useQuery } from '@apollo/client';
import {
    camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct,
    wearerBundleDetail,
    wearerBundleDetailVariables,
    wearerBundleDetail_wearerBundleDetail,
} from 'src/queries/__generated__/wearerBundleDetail';
import mixAndMatchWearerBundleInitialQuery from 'src/queries/mixAndMatchWearerBundleInitialQuery.graphql';
import { useDeps } from 'src/hooks/useDeps';
import { absoluteUrl } from 'src/util/absoluteUrl';
import { useWeareBundlePrices } from 'src/hooks/useWeareBundlePrices';
import { Container, Wrapper } from 'src/components/Layout';
import { BundlePageSkeleton } from 'src/elements/Skeletons/Skeletons';
import { ErrorComponent } from 'src/components/Error/ErrorComponent';
import Bugsnag from '@bugsnag/js';
import { NotFound } from 'src/components/NotFound/NotFound';
import {
    cleanBundleProduct,
    mixAndMatchCleanWearerSummary,
} from 'src/components/ProductDetail/utils/product-detail.types';
import invariant from 'tiny-invariant';
import { mixAndMatchQueryToWearerOptionsMap } from 'src/components/WeareBundleProduct/utils/wearer.types';
import camelcaseKeys from 'camelcase-keys';
import { useHeadTags, escapeAttr } from 'src/core/engine/head';
import { currentUrl } from 'src/util/currentUrl';
import { useIndexSku } from 'src/hooks/useIndexSku';
import { KEYWORDS } from 'src/constants/meta';
import { useKlaviyoViewedBundle } from 'src/features/klaviyo/hooks/useKlaviyoViewedBundle';
import { useSetPopupPageProduct } from 'src/components/PopupBanner/PopupPageContext';

type Props = {
    pathname?: string;
    id: number;
};

type MixAndMatchBundleProps = {
    first: wearerBundleDetail_wearerBundleDetail;
    id: number;
};

export const MixAndMatchBundleRoot: React.FC<Props> = ({ pathname, id }) => {
    useSetResolved();
    /**
     * Make the initial Product Query
     */
    const { loading, error, data } = useQuery<wearerBundleDetail, wearerBundleDetailVariables>(
        mixAndMatchWearerBundleInitialQuery,
        {
            variables: { ids: [id] },
            ssr: useDeps().env.SSR_GQL,
        },
    );

    const first = data?.wearerBundleDetail?.[0];
    const hasCompleteData = data && Array.isArray(data.wearerBundleDetail) && first;

    if (loading || !hasCompleteData) {
        return <BundlePageSkeleton />;
    }

    if (error) {
        return <ErrorComponent message={error?.message} />;
    }

    if (!data) {
        Bugsnag.notify('!data returned true');
        return <NotFound />;
    }

    if (!Array.isArray(data.wearerBundleDetail)) {
        Bugsnag.notify('!Array.isArray(data.wearerBundleDetail) returned true');
        return <NotFound />;
    }

    if (!first) {
        Bugsnag.notify('!first returned true');
        return <NotFound />;
    }

    return <MixAndMatchBundleComponent first={first} id={id} />;
};

const MixAndMatchBundleComponent: React.FC<MixAndMatchBundleProps> = ({ first, id }) => {
    const { DOMAIN } = useDeps().env;

    const prices = useWeareBundlePrices(id);

    const product = cleanBundleProduct(first);
    useSetPopupPageProduct(product.id, product.categories?.[0]?.id ?? null);

    invariant(first.__typename === 'WearerBundlesProduct', 'product type should be WearerBundlesProduct');
    invariant(Array.isArray(first.bundle_items), 'product type should be WearerBundlesProduct');

    const mixAndMatchBundleData = camelcaseKeys(first as any, {
        deep: true,
    }) as unknown as camelized_wearerBundleDetail_wearerBundleDetail_WearerBundlesProduct;

    const wearerOptions = mixAndMatchQueryToWearerOptionsMap(first);
    const summary = mixAndMatchCleanWearerSummary(first.bundle_items);
    const url = currentUrl(DOMAIN!, `/${product.url_key}` || '');

    useKlaviyoViewedBundle({ product, price: prices, url });

    const productName = product.name;
    const title = productName.toLowerCase().includes('workwear express')
        ? productName
        : `${productName} | Workwear Express`;

    const noIndexedProduct = useIndexSku(product.sku);
    const { setHead } = useHeadTags();

    useEffect(() => {
        const desc = product.meta_description || product.meta_title || product.name || '';
        const meta = [
            `<meta name="description" content="${escapeAttr(desc)}" />`,
            noIndexedProduct ? '<meta name="robots" content="noindex" />' : '',
            `<meta property="og:title" content="${escapeAttr(product.name || '')}" />`,
            `<meta property="og:description" content="${escapeAttr(desc)}" />`,
            `<meta property="og:image" content="${escapeAttr(absoluteUrl(DOMAIN!, product.small_image || ''))}" />`,
            `<meta name="keywords" content="${escapeAttr(product.meta_keywords || KEYWORDS)}" />`,
        ].join('');
        const link = `<link rel="canonical" href="${escapeAttr(url)}" />`;
        setHead({ title: `<title>${escapeAttr(title)}</title>`, meta, link });
    }, [title, product, url, noIndexedProduct, setHead, DOMAIN]);

    return (
        <Container>
            <Wrapper variant="page-inner" bg="grey">
                <MixAndMatchBundle
                    bundles={mixAndMatchBundleData.bundleItems}
                    bundleTitle={mixAndMatchBundleData.name}
                    locations={mixAndMatchBundleData.locations}
                    prices={prices}
                    shortDescription={mixAndMatchBundleData.shortDescription}
                    brandBadge={mixAndMatchBundleData.brandBadge?.label}
                    mediaGallery={mixAndMatchBundleData.mediaGallery}
                    sku={mixAndMatchBundleData.sku}
                    wearerOptions={wearerOptions}
                    summary={summary}
                    product={product}
                    id={id}
                />
            </Wrapper>
        </Container>
    );
};
